import React, { useEffect, useRef } from 'react'
import { useMapContext } from '../MapContext'
import ImageMarker from './ImageMarker'
import IconMarker from './IconMarker'
import { hasTouchSupport } from '../../Device'
import s from './Marker.less'
import RubricMarker from './RubricMarker'
import classNames from 'classnames'

import { Marker as MapLibreMarker } from 'maplibre-gl'
import { HTML_MARKER, IMAGE_MARKER } from './MarkerConstants'
import useErrorBoundaryCallback from '../../utils/useErrorBoundaryCallback'

const HTMLMarker = ({ html }) => html

// webpack:BrowserOnly
const Marker = props => {
  const {
    isCurrent,
    type,
    color,
    coordinates: { lng, lat },
    anchorPosition = 'bottom',
    insideSvgSize,
    icon,
    src,
    size,
    height,
    width,
    dragOptions,
    html
  } = props
  const { map } = useMapContext()
  const markerElementRef = useRef()
  const mapLibreMarkerRef = useRef()
  const isDraggable = dragOptions && dragOptions.onDragEnd

  const onClick = e => {
    e.preventDefault()
    e.stopPropagation()
    props.onClick?.(e)
  }

  const onSelect = e => {
    e.preventDefault()
    e.stopPropagation()
    props.onSelect?.(e)
  }

  const onUnselect = e => {
    e.preventDefault()
    e.stopPropagation()
    props.onUnselect?.(e)
  }

  const handleEvents = useErrorBoundaryCallback(
    method => {
      if (mapLibreMarkerRef?.current?.getElement) {
        const mapMarker = mapLibreMarkerRef.current.getElement()
        if (hasTouchSupport()) {
          mapMarker[method]('click', onClick)
        } else {
          mapMarker[method]('mouseenter', onSelect)
          mapMarker[method]('mouseleave', onUnselect)
          if (props.onClick) mapMarker[method]('click', onClick)
        }
      }
    },
    [onClick, onSelect, onUnselect]
  )

  const attachEvents = useErrorBoundaryCallback(() => {
    handleEvents('addEventListener')
    if (isDraggable && mapLibreMarkerRef.current) {
      mapLibreMarkerRef.current.on('dragend', dragOptions.onDragEnd)
    }
  }, [handleEvents])

  const unattachEvents = useErrorBoundaryCallback(() => {
    handleEvents('removeEventListener')
    if (isDraggable && mapLibreMarkerRef.current) {
      mapLibreMarkerRef.current.off('dragend', dragOptions.onDragEnd)
    }
  }, [handleEvents])

  const buildMarker = () => {
    const {
      current: { firstChild }
    } = markerElementRef
    if (!map || !lng || !lat) return

    mapLibreMarkerRef.current = new MapLibreMarker({
      element: firstChild,
      anchor: anchorPosition,
      draggable: Boolean(dragOptions)
    })
      .setLngLat([lng, lat])
      .addTo(map)
  }

  useEffect(() => {
    if (!mapLibreMarkerRef.current) {
      buildMarker()
    }

    return () => {
      if (mapLibreMarkerRef.current) {
        mapLibreMarkerRef.current.remove()
        mapLibreMarkerRef.current = null
      }
    }
  }, [])

  useEffect(() => {
    attachEvents()
    return () => {
      unattachEvents()
    }
  }, [attachEvents, unattachEvents])

  const renderMarkerType = () => {
    switch (type) {
      case IMAGE_MARKER:
        return <ImageMarker {...{ src, height, width }} />
      case HTML_MARKER:
        return <HTMLMarker html={html} />
      default:
        return <IconMarker {...{ icon, color, size, insideSvgSize }} />
    }
  }

  return (
    <div ref={markerElementRef}>
      <div className={classNames(isCurrent ? s.activeMarker : s.marker, isCurrent && 'activeMarker')}>
        {isCurrent && type !== IMAGE_MARKER && (
          <div className={s.pinWrapper} style={{ fill: color }}>
            <RubricMarker />
          </div>
        )}
        <div
          className={classNames(
            isCurrent && type !== IMAGE_MARKER && s.activeMarkerWrapper,
            isCurrent && type === IMAGE_MARKER && s.activeMarkerWrapperImage
          )}
        >
          {renderMarkerType()}
        </div>
      </div>
    </div>
  )
}

export default Marker
